<script setup>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  NCard,
  NForm,
  NFormItem,
  NInput,
  NButton,
  NCheckbox,
  NSelect,
  NSpin,
  NAlert,
  NPageHeader,
  NDivider,
  NUpload,
  NSpace,
  NInputNumber,
  NSteps,
  NStep,
} from "naive-ui";

// API utilities
import { getProvider, claimProviderProfile } from "@/services/provider";
import { authToken, isAuthenticated } from "@/services/auth";

const route = useRoute();
const router = useRouter();
const providerId = route.params.id;

// Page title
const pageTitle = "Claim Provider Profile";

// Form data
const formData = ref({
  fullName: "",
  position: "",
  email: "",
  phoneNumber: "",
  verificationMethod: "document",
  documentType: "license",
  documentNumber: "",
  documentFile: null,
  websiteUrl: "",
  businessEmail: "",
  additionalNotes: "",
  agreeToTerms: false
});

// Provider data
const provider = ref(null);

// Component state
const loading = ref(false);
const submitting = ref(false);
const success = ref(false);
const error = ref(null);
const currentStep = ref(0);

// Verification method options
const verificationMethodOptions = [
  { label: "Official ID/Document", value: "document" },
  { label: "Business Email", value: "email" },
  { label: "Website Ownership", value: "website" }
];

// Document type options
const documentTypeOptions = [
  { label: "Professional License", value: "license" },
  { label: "Business Registration", value: "business" },
  { label: "Tax ID Document", value: "tax" },
  { label: "Other Official Document", value: "other" }
];

// Position options
const positionOptions = [
  { label: "Owner", value: "owner" },
  { label: "Manager", value: "manager" },
  { label: "Authorized Representative", value: "representative" },
  { label: "Medical Staff", value: "medical" },
  { label: "Administrative Staff", value: "administrative" }
];

// Load provider data
onMounted(async () => {
  if (!isAuthenticated.value) {
    router.push({ path: '/login', query: { redirect: route.fullPath } });
    return;
  }

  try {
    loading.value = true;
    const data = await getProvider(providerId);
    
    if (data) {
      provider.value = data;
      
      // If provider is already claimed, redirect back
      if (data.status !== 'unclaimed') {
        error.value = "This provider profile has already been claimed";
        setTimeout(() => {
          router.push(`/providers/${providerId}`);
        }, 3000);
      }
    } else {
      error.value = "Provider not found";
      setTimeout(() => {
        router.push('/providers');
      }, 3000);
    }
  } catch (err) {
    error.value = "Failed to load provider data";
    console.error(err);
  } finally {
    loading.value = false;
  }
});

// Handle file upload
const handleFileUpload = (options) => {
  const { file } = options;
  formData.value.documentFile = file;
  return false; // Prevent default upload behavior
};

// Next step
const nextStep = async () => {
  if (currentStep.value === 0) {
    // Validate first step
    if (!formData.value.fullName || !formData.value.position || !formData.value.email || !formData.value.phoneNumber) {
      error.value = "Please fill out all required fields";
      return;
    }
    error.value = null;
    currentStep.value = 1;
  } else if (currentStep.value === 1) {
    // Validate second step based on verification method
    if (formData.value.verificationMethod === 'document') {
      if (!formData.value.documentType || !formData.value.documentNumber || !formData.value.documentFile) {
        error.value = "Please provide all document verification details";
        return;
      }
    } else if (formData.value.verificationMethod === 'email') {
      if (!formData.value.businessEmail) {
        error.value = "Please provide your business email";
        return;
      }
    } else if (formData.value.verificationMethod === 'website') {
      if (!formData.value.websiteUrl) {
        error.value = "Please provide your website URL";
        return;
      }
    }
    error.value = null;
    currentStep.value = 2;
  }
};

// Previous step
const prevStep = () => {
  if (currentStep.value > 0) {
    currentStep.value--;
  }
};

// Submit claim
const submitClaim = async () => {
  if (!formData.value.agreeToTerms) {
    error.value = "You must agree to the terms and conditions";
    return;
  }

  try {
    error.value = null;
    submitting.value = true;

    // Prepare form data for submission
    const claimData = {
      provider_id: providerId,
      claimant_name: formData.value.fullName,
      claimant_position: formData.value.position,
      claimant_email: formData.value.email,
      claimant_phone: formData.value.phoneNumber,
      verification_method: formData.value.verificationMethod,
      document_type: formData.value.verificationMethod === 'document' ? formData.value.documentType : null,
      document_number: formData.value.verificationMethod === 'document' ? formData.value.documentNumber : null,
      business_email: formData.value.verificationMethod === 'email' ? formData.value.businessEmail : null,
      website_url: formData.value.verificationMethod === 'website' ? formData.value.websiteUrl : null,
      additional_notes: formData.value.additionalNotes
    };

    // Handle file upload if present
    let response;
    if (formData.value.verificationMethod === 'document' && formData.value.documentFile) {
      // Create FormData for file upload
      const formDataObj = new FormData();
      
      // Add the file
      formDataObj.append('document', formData.value.documentFile.file);
      
      // Add other fields
      Object.keys(claimData).forEach(key => {
        if (claimData[key] !== null) {
          formDataObj.append(key, claimData[key]);
        }
      });
      
      // Submit with file
      response = await claimProviderProfile(authToken.value, formDataObj, true);
    } else {
      // Submit without file
      response = await claimProviderProfile(authToken.value, claimData);
    }

    // Handle success
    if (response) {
      success.value = true;
      
      // Redirect after a delay
      setTimeout(() => {
        router.push(`/providers/${providerId}?claimed=pending`);
      }, 5000);
    }
  } catch (err) {
    error.value = err.message || "Failed to submit claim";
    console.error(err);
  } finally {
    submitting.value = false;
  }
};

// Cancel and go back
const handleCancel = () => {
  router.push(`/providers/${providerId}`);
};
</script>

<template>
  <div class="claim-profile-container">
    <NPageHeader :title="pageTitle" @back="handleCancel" />

    <NCard v-if="loading">
      <div class="loading-state">
        <NSpin size="large" />
        <p>Loading provider data...</p>
      </div>
    </NCard>

    <div v-else-if="error && !provider">
      <NAlert type="error" :title="error" />
    </div>

    <div v-else-if="success">
      <NAlert type="success" title="Claim Submitted Successfully">
        <p>Your claim has been submitted for verification. We'll review your information and contact you soon.</p>
        <p class="mt-4">You'll be redirected to the provider profile page in a few seconds...</p>
      </NAlert>
    </div>

    <template v-else-if="provider">
        {{ provider }}
        <NCard class="mb-4">
        <div class="provider-summary">
          <h2 class="text-xl font-bold mb-2">{{ provider.provider_name }}</h2>
          <div class="text-gray-600 mb-1">{{ provider.specializations }}</div>
          <div class="text-gray-600 mb-1">{{ provider.address_line1 }}</div>
          <div class="text-gray-600">{{ provider.phone_numbers }}</div>
        </div>
      </NCard>

      <NCard>
        <!-- Error notification -->
        <NAlert v-if="error" type="error" title="Error" closable class="mb-4">
          {{ error }}
        </NAlert>

        <NSteps :current="currentStep" class="mb-6">
          <NStep title="Personal Information" description="Your contact details" />
          <NStep title="Verification" description="Prove ownership" />
          <NStep title="Review & Submit" description="Final submission" />
        </NSteps>

        <div v-if="currentStep === 0">
          <h2 class="text-lg font-semibold mb-4">Personal Information</h2>
          <NForm label-placement="left" label-width="160px">
            <NFormItem label="Full Name" required>
              <NInput v-model:value="formData.fullName" placeholder="Enter your full name" />
            </NFormItem>

            <NFormItem label="Position" required>
              <NSelect v-model:value="formData.position" :options="positionOptions" 
                placeholder="Select your position" />
            </NFormItem>

            <NFormItem label="Email" required>
              <NInput v-model:value="formData.email" placeholder="Enter your email" />
            </NFormItem>

            <NFormItem label="Phone Number" required>
              <NInput v-model:value="formData.phoneNumber" placeholder="Enter your phone number" />
            </NFormItem>
          </NForm>

          <div class="flex justify-between mt-6">
            <NButton @click="handleCancel">
              Cancel
            </NButton>
            <NButton type="primary" @click="nextStep">
              Next
            </NButton>
          </div>
        </div>

        <div v-else-if="currentStep === 1">
          <h2 class="text-lg font-semibold mb-4">Verification</h2>
          <NForm label-placement="left" label-width="180px">
            <NFormItem label="Verification Method" required>
              <NSelect v-model:value="formData.verificationMethod" :options="verificationMethodOptions" 
                placeholder="Select verification method" />
            </NFormItem>

            <!-- Document verification fields -->
            <template v-if="formData.verificationMethod === 'document'">
              <NFormItem label="Document Type" required>
                <NSelect v-model:value="formData.documentType" :options="documentTypeOptions" 
                  placeholder="Select document type" />
              </NFormItem>

              <NFormItem label="Document Number" required>
                <NInput v-model:value="formData.documentNumber" placeholder="Enter license or ID number" />
              </NFormItem>

              <NFormItem label="Upload Document" required>
                <NUpload 
                  accept=".pdf,.jpg,.jpeg,.png"
                  :custom-request="handleFileUpload"
                  :default-upload="false"
                  list-type="image"
                  max=1
                >
                  <NButton>Upload Document</NButton>
                </NUpload>
                <div class="text-xs text-gray-500 mt-1">
                  Accepted formats: PDF, JPG, JPEG, PNG. Max size: 5MB
                </div>
              </NFormItem>
            </template>

            <!-- Email verification fields -->
            <template v-if="formData.verificationMethod === 'email'">
              <NFormItem label="Business Email" required>
                <NInput v-model:value="formData.businessEmail" 
                  placeholder="Enter your business email (must match domain)" />
                <div class="text-xs text-gray-500 mt-1">
                  Must be an email with the same domain as the provider's website
                </div>
              </NFormItem>
            </template>

            <!-- Website verification fields -->
            <template v-if="formData.verificationMethod === 'website'">
              <NFormItem label="Website URL" required>
                <NInput v-model:value="formData.websiteUrl" 
                  placeholder="Enter your provider website URL" />
                <div class="text-xs text-gray-500 mt-1">
                  You'll need to add a verification code to your website
                </div>
              </NFormItem>
            </template>
          </NForm>

          <div class="flex justify-between mt-6">
            <NButton @click="prevStep">
              Back
            </NButton>
            <NButton type="primary" @click="nextStep">
              Next
            </NButton>
          </div>
        </div>

        <div v-else-if="currentStep === 2">
          <h2 class="text-lg font-semibold mb-4">Review & Submit</h2>
          
          <div class="bg-gray-50 p-4 rounded-lg mb-6">
            <h3 class="font-medium mb-2">Personal Information</h3>
            <div class="grid grid-cols-2 gap-2 mb-4">
              <div class="text-gray-600">Full Name:</div>
              <div>{{ formData.fullName }}</div>
              <div class="text-gray-600">Position:</div>
              <div>{{ formData.position }}</div>
              <div class="text-gray-600">Email:</div>
              <div>{{ formData.email }}</div>
              <div class="text-gray-600">Phone:</div>
              <div>{{ formData.phoneNumber }}</div>
            </div>

            <h3 class="font-medium mb-2">Verification Method</h3>
            <div class="grid grid-cols-2 gap-2">
              <div class="text-gray-600">Method:</div>
              <div>{{ 
                formData.verificationMethod === 'document' ? 'Official Document' : 
                formData.verificationMethod === 'email' ? 'Business Email' : 'Website Ownership' 
              }}</div>
              
              <template v-if="formData.verificationMethod === 'document'">
                <div class="text-gray-600">Document Type:</div>
                <div>{{ 
                  formData.documentType === 'license' ? 'Professional License' : 
                  formData.documentType === 'business' ? 'Business Registration' :
                  formData.documentType === 'tax' ? 'Tax ID Document' : 'Other Official Document'
                }}</div>
                <div class="text-gray-600">Document Number:</div>
                <div>{{ formData.documentNumber }}</div>
                <div class="text-gray-600">File:</div>
                <div>{{ formData.documentFile ? formData.documentFile.name : 'No file selected' }}</div>
              </template>
              
              <template v-if="formData.verificationMethod === 'email'">
                <div class="text-gray-600">Business Email:</div>
                <div>{{ formData.businessEmail }}</div>
              </template>
              
              <template v-if="formData.verificationMethod === 'website'">
                <div class="text-gray-600">Website URL:</div>
                <div>{{ formData.websiteUrl }}</div>
              </template>
            </div>
          </div>

          <NFormItem>
            <NInput 
              v-model:value="formData.additionalNotes" 
              type="textarea" 
              placeholder="Additional notes or information to help verify your claim (optional)"
              :autosize="{ minRows: 2, maxRows: 4 }"
            />
          </NFormItem>

          <NDivider />

          <NFormItem>
            <NCheckbox v-model:checked="formData.agreeToTerms">
              I certify that I am authorized to claim this provider profile and all information 
              provided is accurate and complete. I understand that false information may result 
              in rejection of my claim.
            </NCheckbox>
          </NFormItem>

          <div class="flex justify-between mt-6">
            <NButton @click="prevStep">
              Back
            </NButton>
            <NButton type="primary" @click="submitClaim" :loading="submitting" :disabled="submitting">
              Submit Claim
            </NButton>
          </div>
        </div>
      </NCard>
    </template>
  </div>
</template>

<style scoped>
.claim-profile-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.provider-summary {
  border-left: 4px solid #4096ff;
  padding-left: 16px;
}

h2 {
  margin-top: 0;
  margin-bottom: 20px;
  color: var(--primary-color);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .claim-profile-container {
    padding: 10px;
  }
  
  .grid {
    grid-template-columns: 1fr !important;
  }
}
</style>